import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import coachProfileService from '../../services/coachProfileService';
import teamService from '../../services/teamService';

const initialState = {
  profile: null,
  loading: false,
  error: null,
  updateLoading: false,
  profileImageLoading: false,
  updateError: null,
  // Dashboard data
  dashboardData: null,
  dashboardLoading: false,
  dashboardError: null,
  suggestions: [],
  topPlayers: [],
  followedPlayers: [],
  // Uncommitted Tracker
  uncommittedPlayers: [],
  uncommittedPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasMore: false,
  },
  uncommittedLoading: false,
  uncommittedError: null,
  // Statistics
  statisticsTopPlayers: [],
  statisticsPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasMore: false,
  },
  statisticsLoading: false,
  statisticsError: null,
  // Followed Teams
  followedTeams: [],
  followedTeamsLoading: false,
  followedTeamsError: null,
  // Team Roster
  teamRoster: [],
  teamRosterLoading: false,
  teamRosterError: null,
  selectedTeam: null,
  teamRosterPagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasMore: false,
  },
};

export const getCoachProfile = createAsyncThunk(
  'coachProfile/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getProfile();
      return data.coach;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch coach profile.'
      );
    }
  }
);

export const updateCoachProfile = createAsyncThunk(
  'coachProfile/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.updateProfile(profileData);
      return data.coach;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update coach profile.'
      );
    }
  }
);

// Profile Image Actions
export const updateCoachProfileImage = createAsyncThunk(
  'coachProfile/updateProfileImage',
  async (imageFile, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.updateProfileImage(imageFile);
      return data.coach;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update profile image.'
      );
    }
  }
);

export const deleteCoachProfileImage = createAsyncThunk(
  'coachProfile/deleteProfileImage',
  async (_, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.deleteProfileImage();
      return data.coach;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete profile image.'
      );
    }
  }
);

// Dashboard Actions
export const getCoachDashboard = createAsyncThunk(
  'coachProfile/getDashboard',
  async (params = { page: 1, limit: 20 }, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getDashboard(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch dashboard data.'
      );
    }
  }
);

export const getCoachSuggestions = createAsyncThunk(
  'coachProfile/getSuggestions',
  async (params = { limit: 10 }, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getSuggestions(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch suggestions.'
      );
    }
  }
);

// Follow/Unfollow Actions
export const followPlayer = createAsyncThunk(
  'coachProfile/followPlayer',
  async (playerId, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.followPlayer(playerId);
      return { playerId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to follow player.'
      );
    }
  }
);

export const unfollowPlayer = createAsyncThunk(
  'coachProfile/unfollowPlayer',
  async (playerId, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.unfollowPlayer(playerId);
      return { playerId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to unfollow player.'
      );
    }
  }
);

// Follow/Unfollow Team Actions
export const followTeam = createAsyncThunk(
  'coachProfile/followTeam',
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.followTeam(teamId);
      return { teamId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to follow team.'
      );
    }
  }
);

export const unfollowTeam = createAsyncThunk(
  'coachProfile/unfollowTeam',
  async (teamId, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.unfollowTeam(teamId);
      return { teamId, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to unfollow team.'
      );
    }
  }
);

export const fetchFollowedTeams = createAsyncThunk(
  'coachProfile/fetchFollowedTeams',
  async (params = { page: 1, limit: 20 }, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getFollowedTeams(params.page, params.limit);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch followed teams.'
      );
    }
  }
);

// Uncommitted Tracker Actions
export const fetchUncommittedPlayers = createAsyncThunk(
  'coachProfile/fetchUncommittedPlayers',
  async (params = { page: 1, limit: 5000 }, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getUncommittedPlayers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch uncommitted players.'
      );
    }
  }
);

export const loadMoreUncommittedPlayers = createAsyncThunk(
  'coachProfile/loadMoreUncommittedPlayers',
  async (params, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getUncommittedPlayers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more uncommitted players.'
      );
    }
  }
);

// Statistics Actions
export const fetchTopPlayers = createAsyncThunk(
  'coachProfile/fetchTopPlayers',
  async (params = { limit: 10 }, { rejectWithValue }) => {
    try {
      const data = await coachProfileService.getTopPlayers(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch top players.'
      );
    }
  }
);

// Team Roster Actions
export const fetchTeamRoster = createAsyncThunk(
  'coachProfile/fetchTeamRoster',
  async ({ teamId, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      const data = await teamService.getCoachTeamRoster(teamId, page, limit);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch team roster.'
      );
    }
  }
);

export const loadMoreTeamRoster = createAsyncThunk(
  'coachProfile/loadMoreTeamRoster',
  async ({ teamId, page, limit = 50 }, { rejectWithValue }) => {
    try {
      const data = await teamService.getCoachTeamRoster(teamId, page, limit);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to load more team roster.'
      );
    }
  }
);

const coachProfileSlice = createSlice({
  name: 'coachProfile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
    clearProfile: (state) => {
      state.profile = null;
      state.error = null;
      state.updateError = null;
    },
    setSelectedTeam: (state, action) => {
      state.selectedTeam = action.payload;
    },
    clearTeamRoster: (state) => {
      state.teamRoster = [];
      state.selectedTeam = null;
      state.teamRosterPagination = {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 50,
        hasMore: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Coach Profile
      .addCase(getCoachProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCoachProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(getCoachProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Coach Profile
      .addCase(updateCoachProfile.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateCoachProfile.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(updateCoachProfile.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Update Profile Image
      .addCase(updateCoachProfileImage.pending, (state) => {
        state.profileImageLoading = true;
        state.updateError = null;
      })
      .addCase(updateCoachProfileImage.fulfilled, (state, action) => {
        state.profileImageLoading = false;
        state.profile = action.payload;
        state.updateError = null;
        // Sync localStorage so sidebar reflects immediately
        const stored = JSON.parse(localStorage.getItem('coachProfile') || '{}');
        stored.profileImage = action.payload.profileImage;
        localStorage.setItem('coachProfile', JSON.stringify(stored));
      })
      .addCase(updateCoachProfileImage.rejected, (state, action) => {
        state.profileImageLoading = false;
        state.updateError = action.payload;
      })
      // Delete Profile Image
      .addCase(deleteCoachProfileImage.pending, (state) => {
        state.profileImageLoading = true;
        state.updateError = null;
      })
      .addCase(deleteCoachProfileImage.fulfilled, (state, action) => {
        state.profileImageLoading = false;
        state.profile = action.payload;
        state.updateError = null;
        // Sync localStorage so sidebar reflects immediately
        const stored = JSON.parse(localStorage.getItem('coachProfile') || '{}');
        stored.profileImage = action.payload.profileImage || null;
        localStorage.setItem('coachProfile', JSON.stringify(stored));
      })
      .addCase(deleteCoachProfileImage.rejected, (state, action) => {
        state.profileImageLoading = false;
        state.updateError = action.payload;
      })
      // Get Coach Dashboard
      .addCase(getCoachDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getCoachDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardData = action.payload;
        state.profile = action.payload.coach;
        state.suggestions = action.payload.suggestions || [];
        state.topPlayers = action.payload.topPlayers || [];
        state.followedPlayers = action.payload.followedPlayers || [];
        state.dashboardError = null;
        console.log('Dashboard Data:', action.payload);
        console.log('coach Data:', state.profile);

        const coach = action.payload.coach;

        const coachProfile = {
          name: `${coach.firstName} ${coach.lastName}`,
          profileImage: coach.profileImage,
          position: coach.position,
          jobTitle: coach.jobTitle,
          organization: coach.organization
        };

        localStorage.setItem("coachProfile", JSON.stringify(coachProfile));

      })
      .addCase(getCoachDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })
      // Get Coach Suggestions
      .addCase(getCoachSuggestions.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getCoachSuggestions.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.suggestions = action.payload.suggestions || action.payload || [];
        state.dashboardError = null;
      })
      .addCase(getCoachSuggestions.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })
      // Follow Player
      .addCase(followPlayer.pending, (state, action) => {
        state.error = null;
        const playerId = action.meta.arg;
        // Optimistically update in uncommitted players
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = true;
        }
        // Also update in statistics top players
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = true;
        }
        // Also update in dashboard followed players
        const dashboardPlayer = state.followedPlayers.find(p => p._id === playerId);
        if (dashboardPlayer) {
          dashboardPlayer.isFollowing = true;
        }
        // Also update in top players
        const topPlayer = state.topPlayers.find(p => p._id === playerId);
        if (topPlayer) {
          topPlayer.isFollowing = true;
        }
        // Also update in suggestions
        const suggestionPlayer = state.suggestions.find(p => p._id === playerId);
        if (suggestionPlayer) {
          suggestionPlayer.isFollowing = true;
        }
      })
      .addCase(followPlayer.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(followPlayer.rejected, (state, action) => {
        state.error = action.payload;
        const playerId = action.meta.arg;
        // Revert optimistic update in all lists
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = false;
        }
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = false;
        }
        const dashboardPlayer = state.followedPlayers.find(p => p._id === playerId);
        if (dashboardPlayer) {
          dashboardPlayer.isFollowing = false;
        }
        const topPlayer = state.topPlayers.find(p => p._id === playerId);
        if (topPlayer) {
          topPlayer.isFollowing = false;
        }
        const suggestionPlayer = state.suggestions.find(p => p._id === playerId);
        if (suggestionPlayer) {
          suggestionPlayer.isFollowing = false;
        }
      })
      // Unfollow Player
      .addCase(unfollowPlayer.pending, (state, action) => {
        state.error = null;
        const playerId = action.meta.arg;
        // Optimistically update in uncommitted players
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = false;
        }
        // Also update in statistics top players
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = false;
        }
        // Also update in dashboard followed players
        const dashboardPlayer = state.followedPlayers.find(p => p._id === playerId);
        if (dashboardPlayer) {
          dashboardPlayer.isFollowing = false;
        }
        // Also update in top players
        const topPlayer = state.topPlayers.find(p => p._id === playerId);
        if (topPlayer) {
          topPlayer.isFollowing = false;
        }
        // Also update in suggestions
        const suggestionPlayer = state.suggestions.find(p => p._id === playerId);
        if (suggestionPlayer) {
          suggestionPlayer.isFollowing = false;
        }
      })
      .addCase(unfollowPlayer.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(unfollowPlayer.rejected, (state, action) => {
        state.error = action.payload;
        const playerId = action.meta.arg;
        // Revert optimistic update in all lists
        const uncommittedPlayer = state.uncommittedPlayers.find(p => p._id === playerId);
        if (uncommittedPlayer) {
          uncommittedPlayer.isFollowing = true;
        }
        const statsPlayer = state.statisticsTopPlayers.find(p => p._id === playerId);
        if (statsPlayer) {
          statsPlayer.isFollowing = true;
        }
        const dashboardPlayer = state.followedPlayers.find(p => p._id === playerId);
        if (dashboardPlayer) {
          dashboardPlayer.isFollowing = true;
        }
        const topPlayer = state.topPlayers.find(p => p._id === playerId);
        if (topPlayer) {
          topPlayer.isFollowing = true;
        }
        const suggestionPlayer = state.suggestions.find(p => p._id === playerId);
        if (suggestionPlayer) {
          suggestionPlayer.isFollowing = true;
        }
      })
      // Follow Team
      .addCase(followTeam.pending, (state) => {
        state.error = null;
      })
      .addCase(followTeam.fulfilled, (state) => {
        state.error = null;
        // Optionally add to followed teams list
        // This will be refreshed on next fetch
      })
      .addCase(followTeam.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Unfollow Team
      .addCase(unfollowTeam.pending, (state) => {
        state.error = null;
      })
      .addCase(unfollowTeam.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(unfollowTeam.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Fetch Followed Teams
      .addCase(fetchFollowedTeams.pending, (state) => {
        state.followedTeamsLoading = true;
        state.followedTeamsError = null;
      })
      .addCase(fetchFollowedTeams.fulfilled, (state, action) => {
        state.followedTeamsLoading = false;
        state.followedTeams = action.payload.teams || action.payload.data || action.payload || [];
        state.followedTeamsError = null;
      })
      .addCase(fetchFollowedTeams.rejected, (state, action) => {
        state.followedTeamsLoading = false;
        state.followedTeamsError = action.payload;
      })
      // Fetch Uncommitted Players
      .addCase(fetchUncommittedPlayers.pending, (state) => {
        state.uncommittedLoading = true;
        state.uncommittedError = null;
      })
      .addCase(fetchUncommittedPlayers.fulfilled, (state, action) => {
        state.uncommittedLoading = false;
        state.uncommittedPlayers = action.payload.players || action.payload.data || [];
        const currentPage = action.payload.pagination?.currentPage || action.payload.currentPage || 1;
        const totalPages = action.payload.pagination?.totalPages || action.payload.totalPages || 1;
        state.uncommittedPagination = {
          currentPage,
          totalPages,
          totalCount: action.payload.pagination?.totalCount || action.payload.totalPlayers || action.payload.total || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 50,
          hasMore: currentPage < totalPages,
        };
        state.uncommittedError = null;
      })
      .addCase(fetchUncommittedPlayers.rejected, (state, action) => {
        state.uncommittedLoading = false;
        state.uncommittedError = action.payload;
      })
      // Load More Uncommitted Players
      .addCase(loadMoreUncommittedPlayers.pending, (state) => {
        state.uncommittedLoading = true;
        state.uncommittedError = null;
      })
      .addCase(loadMoreUncommittedPlayers.fulfilled, (state, action) => {
        state.uncommittedLoading = false;
        const newPlayers = action.payload.players || action.payload.data || [];
        state.uncommittedPlayers = [...state.uncommittedPlayers, ...newPlayers];
        state.uncommittedPagination = {
          currentPage: action.payload.pagination?.currentPage || action.payload.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || action.payload.totalPages || 1,
          totalCount: action.payload.pagination?.totalCount || action.payload.total || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 10,
          hasMore: action.payload.pagination?.hasMore || false,
        };
        state.uncommittedError = null;
      })
      .addCase(loadMoreUncommittedPlayers.rejected, (state, action) => {
        state.uncommittedLoading = false;
        state.uncommittedError = action.payload;
      })
      // Fetch Top Players for Statistics
      .addCase(fetchTopPlayers.pending, (state) => {
        state.statisticsLoading = true;
        state.statisticsError = null;
      })
      .addCase(fetchTopPlayers.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsTopPlayers = action.payload.players || action.payload.data || action.payload || [];
        const currentPage = action.payload.pagination?.currentPage || action.payload.currentPage || 1;
        const totalPages = action.payload.pagination?.totalPages || action.payload.totalPages || 1;
        state.statisticsPagination = {
          currentPage,
          totalPages,
          totalCount: action.payload.pagination?.totalCount || action.payload.totalPlayers || action.payload.total || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 50,
          hasMore: currentPage < totalPages,
        };
        state.statisticsError = null;
      })
      .addCase(fetchTopPlayers.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsError = action.payload;
      })
      // Fetch Team Roster
      .addCase(fetchTeamRoster.pending, (state) => {
        state.teamRosterLoading = true;
        state.teamRosterError = null;
      })
      .addCase(fetchTeamRoster.fulfilled, (state, action) => {
        state.teamRosterLoading = false;
        state.teamRoster = action.payload.players || action.payload.data || [];
        state.teamRosterPagination = {
          currentPage: action.payload.pagination?.currentPage || action.payload.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || action.payload.totalPages || 1,
          totalCount: action.payload.pagination?.totalCount || action.payload.total || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 50,
          hasMore: action.payload.pagination?.hasMore || false,
        };
        state.teamRosterError = null;
      })
      .addCase(fetchTeamRoster.rejected, (state, action) => {
        state.teamRosterLoading = false;
        state.teamRosterError = action.payload;
      })
      // Load More Team Roster
      .addCase(loadMoreTeamRoster.pending, (state) => {
        state.teamRosterLoading = true;
        state.teamRosterError = null;
      })
      .addCase(loadMoreTeamRoster.fulfilled, (state, action) => {
        state.teamRosterLoading = false;
        const newPlayers = action.payload.players || action.payload.data || [];
        state.teamRoster = [...state.teamRoster, ...newPlayers];
        state.teamRosterPagination = {
          currentPage: action.payload.pagination?.currentPage || action.payload.currentPage || 1,
          totalPages: action.payload.pagination?.totalPages || action.payload.totalPages || 1,
          totalCount: action.payload.pagination?.totalCount || action.payload.total || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 50,
          hasMore: action.payload.pagination?.hasMore || false,
        };
        state.teamRosterError = null;
      })
      .addCase(loadMoreTeamRoster.rejected, (state, action) => {
        state.teamRosterLoading = false;
        state.teamRosterError = action.payload;
      });
  },
});

export const { clearError, clearProfile, setSelectedTeam, clearTeamRoster } = coachProfileSlice.actions;
export default coachProfileSlice.reducer;
